/**
 * Custom Hook: useAuditLogs
 * Gere l'etat et les operations pour les logs d'audit
 *
 * Migrated to TanStack Query (useQuery)
 * Backward compatible: same hook name + same return interface
 */

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  auditLogsAPI,
  AuditLog,
  AuditStats,
  GetAuditLogsParams,
} from '../utils/auditLogsAPI';
import { useAuth } from './useAuth';
import { logger } from '@/utils/logger';

// ============================================
// QUERY KEYS
// ============================================

export const auditLogKeys = {
  all: ['audit-logs'] as const,
  logs: (params?: GetAuditLogsParams) =>
    [...auditLogKeys.all, 'list', params] as const,
  statistics: (startDate?: string, endDate?: string) =>
    [...auditLogKeys.all, 'statistics', startDate, endDate] as const,
  resourceHistory: (resourceType: string, resourceId: string) =>
    [...auditLogKeys.all, 'resource-history', resourceType, resourceId] as const,
  userHistory: (userId: string, limit?: number) =>
    [...auditLogKeys.all, 'user-history', userId, limit] as const,
};

// ============================================
// INTERFACES (unchanged for backward compatibility)
// ============================================

export interface UseAuditLogsOptions {
  /**
   * Charger automatiquement les logs au montage du composant
   * @default false
   */
  autoLoad?: boolean;

  /**
   * Parametres par defaut pour le chargement automatique
   */
  defaultParams?: GetAuditLogsParams;
}

export interface UseAuditLogsReturn {
  /**
   * Liste des logs d'audit
   */
  logs: AuditLog[];

  /**
   * Pagination des logs
   */
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  } | null;

  /**
   * Statistiques d'audit
   */
  statistics: AuditStats | null;

  /**
   * Etat de chargement
   */
  loading: boolean;

  /**
   * Message d'erreur
   */
  error: string | null;

  /**
   * Charger les logs d'audit avec filtres optionnels
   */
  loadLogs: (params?: GetAuditLogsParams) => Promise<void>;

  /**
   * Charger les statistiques d'audit
   */
  loadStatistics: (startDate?: string, endDate?: string) => Promise<void>;

  /**
   * Charger l'historique d'audit pour une ressource specifique
   */
  loadResourceHistory: (resourceType: string, resourceId: string) => Promise<AuditLog[]>;

  /**
   * Charger l'historique d'audit pour un utilisateur specifique
   */
  loadUserHistory: (userId: string, limit?: number) => Promise<AuditLog[]>;

  /**
   * Reinitialiser l'etat (logs, stats, erreur)
   */
  reset: () => void;

  /**
   * Effacer le message d'erreur
   */
  clearError: () => void;
}

/**
 * Hook pour gerer les logs d'audit
 *
 * @example
 * ```tsx
 * function AuditLogsComponent() {
 *   const {
 *     logs,
 *     pagination,
 *     statistics,
 *     loading,
 *     error,
 *     loadLogs,
 *     loadStatistics,
 *   } = useAuditLogs({ autoLoad: true });
 *
 *   return (
 *     <div>
 *       {loading && <p>Chargement...</p>}
 *       {error && <p className="error">{error}</p>}
 *       {logs.map(log => (
 *         <div key={log.id}>{log.action} - {log.entityType}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuditLogs(options?: UseAuditLogsOptions): UseAuditLogsReturn {
  const { autoLoad = false, defaultParams } = options || {};
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const companyId = user?.companyId;
  const queryClient = useQueryClient();

  // Local state for imperative parameters
  const [logParams, setLogParams] = useState<GetAuditLogsParams | undefined>(defaultParams);
  const [statsDates, setStatsDates] = useState<{ startDate?: string; endDate?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [isReset, setIsReset] = useState(false);

  // ============================================
  // QUERIES
  // ============================================

  /**
   * Logs list query
   */
  const logsQuery = useQuery({
    queryKey: auditLogKeys.logs(logParams),
    queryFn: () => auditLogsAPI.getLogs(logParams),
    enabled: !!tenantId && (autoLoad || logParams !== defaultParams) && !isReset,
    staleTime: 30 * 1000, // 30 seconds for audit logs (more fresh)
  });

  /**
   * Statistics query
   */
  const statsQuery = useQuery({
    queryKey: auditLogKeys.statistics(statsDates.startDate, statsDates.endDate),
    queryFn: () => auditLogsAPI.getStats(statsDates.startDate, statsDates.endDate),
    enabled: !!tenantId && autoLoad && !isReset,
    staleTime: 60 * 1000, // 1 minute
  });

  // ============================================
  // IMPERATIVE ACTION WRAPPERS (backward compat)
  // ============================================

  /**
   * Charger les logs d'audit
   */
  const loadLogs = useCallback(async (params?: GetAuditLogsParams) => {
    try {
      setError(null);
      setIsReset(false);
      setLogParams(params);
      await queryClient.fetchQuery({
        queryKey: auditLogKeys.logs(params),
        queryFn: () => auditLogsAPI.getLogs(params),
        staleTime: 0,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Erreur lors du chargement des logs d\'audit';
      setError(errorMessage);
      logger.error('Error loading audit logs:', err);
    }
  }, [queryClient]);

  /**
   * Charger les statistiques d'audit
   */
  const loadStatistics = useCallback(async (startDate?: string, endDate?: string) => {
    try {
      setError(null);
      setStatsDates({ startDate, endDate });
      await queryClient.fetchQuery({
        queryKey: auditLogKeys.statistics(startDate, endDate),
        queryFn: () => auditLogsAPI.getStats(startDate, endDate),
        staleTime: 0,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Erreur lors du chargement des statistiques';
      setError(errorMessage);
      logger.error('Error loading audit statistics:', err);
    }
  }, [queryClient]);

  /**
   * Charger l'historique d'une ressource specifique
   */
  const loadResourceHistory = useCallback(
    async (resourceType: string, resourceId: string): Promise<AuditLog[]> => {
      try {
        setError(null);
        return await queryClient.fetchQuery({
          queryKey: auditLogKeys.resourceHistory(resourceType, resourceId),
          queryFn: () => auditLogsAPI.getResourceHistory(resourceType, resourceId),
          staleTime: 30 * 1000,
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.message || 'Erreur lors du chargement de l\'historique';
        setError(errorMessage);
        logger.error('Error loading resource history:', err);
        return [];
      }
    },
    [queryClient]
  );

  /**
   * Charger l'historique d'un utilisateur specifique
   */
  const loadUserHistory = useCallback(
    async (userId: string, limit?: number): Promise<AuditLog[]> => {
      try {
        setError(null);
        return await queryClient.fetchQuery({
          queryKey: auditLogKeys.userHistory(userId, limit),
          queryFn: () => auditLogsAPI.getUserHistory(userId, limit),
          staleTime: 30 * 1000,
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.message || 'Erreur lors du chargement de l\'historique utilisateur';
        setError(errorMessage);
        logger.error('Error loading user history:', err);
        return [];
      }
    },
    [queryClient]
  );

  /**
   * Reinitialiser l'etat
   */
  const reset = useCallback(() => {
    setLogParams(defaultParams);
    setStatsDates({});
    setError(null);
    setIsReset(true);
    queryClient.removeQueries({ queryKey: auditLogKeys.all });
  }, [defaultParams, queryClient]);

  /**
   * Effacer le message d'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================
  // COMPUTED STATE
  // ============================================

  const loading = logsQuery.isLoading || statsQuery.isLoading;

  const combinedError =
    error ||
    (logsQuery.error ? (logsQuery.error instanceof Error ? logsQuery.error.message : 'Erreur lors du chargement des logs') : null) ||
    (statsQuery.error ? (statsQuery.error instanceof Error ? statsQuery.error.message : 'Erreur lors du chargement des statistiques') : null);

  return {
    logs: logsQuery.data?.logs || [],
    pagination: logsQuery.data?.pagination || null,
    statistics: statsQuery.data || null,
    loading,
    error: combinedError,
    loadLogs,
    loadStatistics,
    loadResourceHistory,
    loadUserHistory,
    reset,
    clearError,
  };
}
